/**
 * Shadow-DOM-safe event containment (ADR-034, hazard H1).
 *
 * A `document`-level listener cannot use `node.contains(event.target)` to decide whether
 * an event originated inside `node`, because events crossing a shadow boundary are
 * RETARGETED: `event.target` becomes the shadow HOST, not the inner element. For a node
 * inside the shadow root, `node.contains(host)` is always false — so every click inside the
 * widget reads as a click outside it, and click-outside handlers close popups instantly.
 *
 * `composedPath()` returns the un-retargeted path (including nodes inside shadow roots), so it is
 * correct in BOTH DOM contexts. That makes it safe on the booking results page, which renders the
 * same widget in the light DOM.
 *
 * CALL IT SYNCHRONOUSLY, INSIDE THE LISTENER. `composedPath()` is a snapshot taken at dispatch:
 * it returns `[]` once dispatch has completed, and its path reflects the tree AS IT WAS when the
 * event fired. So it is equivalent to `contains(event.target)` for targets attached at dispatch
 * time — but NOT for a target detached mid-dispatch (an earlier listener re-rendering it away),
 * where `contains()` would say "outside" and `composedPath()` says "inside". That case is
 * unreachable here (the widget's day cells use `onClick`, so nothing unmounts during `mousedown`),
 * but the equivalence is narrower than "identical", and saying so keeps the next reader honest.
 */
export function eventOriginatedInside(
  event: Event,
  node: Node | null | undefined
): boolean {
  if (!node) return false

  // composedPath() is the correct check in BOTH light and shadow DOM.
  if (typeof event.composedPath === 'function') {
    return event.composedPath().includes(node)
  }

  // Fallback for environments without composedPath (light DOM only — no shadow root can
  // exist in such an environment, so contains() is still correct there).
  return node.contains(event.target as Node)
}
